import type { EnglishQuestion as EnglishQuestionType } from '@/lib/englishQuestions';
import { englishTypeMeta } from '@/lib/englishQuestions';

interface Props {
  questions: EnglishQuestionType[];
  current: number;
  answers: Record<string, number>;
  onSelect: (questionId: string, optionIdx: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSubmit: () => void;
}

const optionLabels = ['A', 'B', 'C', 'D'];

export default function EnglishQuestion({
  questions,
  current,
  answers,
  onSelect,
  onNext,
  onPrev,
  onSubmit,
}: Props) {
  const question = questions[current];
  const total = questions.length;
  const meta = englishTypeMeta.find((t) => t.id === question.type);
  const progress = ((current + 1) / total) * 100;
  const isLast = current === total - 1;
  const selected = answers[question.id];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 bg-accent-50 border border-accent-200 px-2.5 py-1 rounded-full">
            <i className={meta?.icon || 'ri-question-line'}></i>
            {meta?.label}
          </span>
          <span className="text-sm text-foreground-500 font-medium">
            {current + 1} / {total}
          </span>
        </div>
        <div className="w-full bg-background-200 rounded-full h-2 overflow-hidden">
          <div
            className="bg-accent-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
        <div className="text-xs text-foreground-400 mb-3 font-medium">
          {question.type === 'meaning' && '다음 영단어의 뜻으로 가장 알맞은 것을 고르세요.'}
          {question.type === 'reverse' && '다음 뜻을 가진 영단어를 고르세요.'}
          {question.type === 'synonym' && '다음 영단어와 뜻이 같은 유의어를 고르세요.'}
          {question.type === 'antonym' && '다음 영단어와 뜻이 반대인 반의어를 고르세요.'}
          {question.type === 'cloze' && '빈칸에 들어갈 가장 알맞은 영단어를 고르세요.'}
        </div>

        {/* Prompt */}
        <div className="mb-6">
          {question.type === 'cloze' ? (
            <p className="text-lg md:text-xl text-foreground-950 leading-relaxed">
              {question.prompt.split('___').map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span className="inline-block min-w-16 border-b-2 border-accent-500 mx-1 text-center font-semibold text-accent-600">
                      ?
                    </span>
                  )}
                </span>
              ))}
            </p>
          ) : (
            <div className="flex items-center justify-center py-6">
              <span className="text-3xl md:text-4xl font-bold text-foreground-950">
                {question.prompt}
              </span>
            </div>
          )}
        </div>

        {/* Options */}
        <div className="space-y-2.5">
          {question.options.map((opt, idx) => {
            const active = selected === idx;
            return (
              <button
                key={idx}
                onClick={() => onSelect(question.id, idx)}
                className={`w-full flex items-center gap-3 text-left rounded-lg border px-4 py-3 transition-colors cursor-pointer ${
                  active
                    ? 'border-accent-500 bg-accent-50 ring-2 ring-accent-300'
                    : 'border-background-200 bg-white hover:border-accent-300'
                }`}
              >
                <span
                  className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 ${
                    active ? 'bg-accent-500 text-white' : 'bg-background-100 text-foreground-600'
                  }`}
                >
                  {optionLabels[idx]}
                </span>
                <span className="text-foreground-900 font-medium">{opt}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6 gap-3">
        <button
          onClick={onPrev}
          disabled={current === 0}
          className="whitespace-nowrap inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-foreground-600 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <i className="ri-arrow-left-line"></i>
          이전
        </button>

        {isLast ? (
          <button
            onClick={onSubmit}
            className="whitespace-nowrap inline-flex items-center gap-1.5 px-6 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-semibold transition-colors cursor-pointer"
          >
            결과 보기
            <i className="ri-check-line"></i>
          </button>
        ) : (
          <button
            onClick={onNext}
            className="whitespace-nowrap inline-flex items-center gap-1.5 px-6 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-semibold transition-colors cursor-pointer"
          >
            다음
            <i className="ri-arrow-right-line"></i>
          </button>
        )}
      </div>
    </div>
  );
}